/**
 * Toasts, backed by sonner.
 *
 * The `useToast().push` / `pushToast` API is unchanged so every existing
 * mutation handler keeps working; only the rendering moved. The actual stack is
 * `<Toaster>` in App.tsx — `ToastContainer` is kept as a no-op so the older
 * layout code that mounts it still compiles.
 */

import { toast } from 'sonner';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastInput {
  type: ToastType;
  message: string;
  /** Auto-dismiss after this many ms. Pass 0 for sticky. Defaults to 4500. */
  ttl?: number;
}

function emit({ type, message, ttl = 4500 }: ToastInput): void {
  const options = { duration: ttl === 0 ? Infinity : ttl };
  if (type === 'success') toast.success(message, options);
  else if (type === 'error') toast.error(message, options);
  else toast.info(message, options);
}

/**
 * Module-level toast helper. Returns `{ push }`. Safe to call from anywhere
 * (mutation `onSuccess` / `onError`, route guards, etc.).
 */
export function useToast(): { push: (input: ToastInput) => void } {
  return { push: emit };
}

/**
 * Imperative push, identical to `useToast().push`. Convenient outside React.
 */
export function pushToast(input: ToastInput): void {
  emit(input);
}

/** @deprecated The stack is rendered by `<Toaster>` in App.tsx. */
export function ToastContainer() {
  return null;
}
